/// Shared top bar used by settings / auth / rank / user / controls.
/// Matches mobile PixelSubpageScaffold: back arrow, centered title, scroll shadow.

import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { useThemeColors } from '@/settings/handler/settingsStore';
import { PixelIcon } from '@/ui/pixel/PixelIcon';

export interface PageChromeProps {
  title: string;
  children: ReactNode;
  /** When set, a 2px rule under the title strengthens as the list scrolls. */
  scrolledUnder?: boolean;
  /** Defaults to browser back. Auth uses this to step backward in the flow. */
  onBack?: () => void;
  /** When false, hides the back control (e.g. required username step). */
  showBack?: boolean;
}

export function PageChrome({
  title,
  children,
  scrolledUnder = false,
  onBack,
  showBack = true,
}: PageChromeProps) {
  const colors = useThemeColors();
  const navigate = useNavigate();
  const ruleAlpha = scrolledUnder ? 1 : 0.28;

  // Auth wizard steps need custom back — intercept the browser gesture when
  // there is nothing to pop (deep links often have a single history entry).
  useEffect(() => {
    if (!showBack || !onBack) return;

    const onPopState = (): void => {
      onBack();
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [showBack, onBack]);

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden"
      style={{ backgroundColor: colors.surface }}
    >
      <div className="flex h-full w-full min-h-0 flex-1 flex-col">
        <div className="shrink-0" style={{ backgroundColor: colors.surface }}>
          <div style={{ height: 12 }} />
          <div className="relative flex items-center justify-center px-2" style={{ height: 43 }}>
            {showBack && (
              <button
                type="button"
                aria-label="Back"
                onClick={onBack ?? (() => navigate(-1))}
                className="absolute left-2 flex items-center justify-center"
                style={{ width: 43, height: 43 }}
              >
                <PixelIcon name="arrow_left" size={24} color={colors.onSurface} />
              </button>
            )}
            <span className="leading-none" style={{ fontSize: 28, color: colors.onSurface }}>
              {title}
            </span>
          </div>
          <div style={{ height: 10 }} />
          <div style={{ height: 2, backgroundColor: colors.onSurface, opacity: ruleAlpha }} />
          <div
            className="overflow-hidden transition-[max-height] duration-[140ms] ease-out"
            style={{ maxHeight: scrolledUnder ? 8 : 0 }}
          >
            <div style={{ height: 2, backgroundColor: colors.onSurface, opacity: 0.12 }} />
            <div style={{ height: 2, backgroundColor: colors.onSurface, opacity: 0.05 }} />
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
      </div>
    </div>
  );
}
